import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Calculator, ArrowRight } from "@phosphor-icons/react";
import { Reveal } from "@/components/Reveal";

const inr = (n) =>
  "₹" + Math.round(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const toWords = (n) => {
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  return inr(n);
};

const sliderCls = "w-full accent-[#C08A45] cursor-pointer";

export const EmiCalculator = () => {
  const [amount, setAmount] = useState(4000000);
  const [rate, setRate] = useState(8.5);
  const [years, setYears] = useState(20);

  const { emi, interest, total, principalPct } = useMemo(() => {
    const r = rate / 1200;
    const n = years * 12;
    const e = r === 0 ? amount / n : (amount * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    const t = e * n;
    return { emi: e, interest: t - amount, total: t, principalPct: (amount / t) * 100 };
  }, [amount, rate, years]);

  const Field = ({ label, display, min, max, step, value, onChange, testid, minLabel, maxLabel }) => (
    <div>
      <div className="flex items-baseline justify-between">
        <label className="text-xs font-bold tracking-[0.15em] uppercase text-moss">{label}</label>
        <span className="font-serif text-xl text-ink" data-testid={`${testid}-value`}>{display}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className={`${sliderCls} mt-3`} data-testid={`${testid}-slider`} />
      <div className="mt-1 flex justify-between text-xs text-moss/70">
        <span>{minLabel}</span>
        <span>{maxLabel}</span>
      </div>
    </div>
  );

  return (
    <section id="emi-calculator" className="py-24 md:py-32 bg-sage relative overflow-hidden" data-testid="emi-calculator-section">
      <div className="absolute -bottom-40 -right-40 w-[480px] h-[480px] rounded-full bg-mint-pale blur-3xl opacity-70" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        <Reveal>
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-mint">Plan your loan</p>
          <h2 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight text-ink max-w-2xl text-balance">
            Know your EMI before you sign
          </h2>
          <p className="mt-5 text-base sm:text-lg text-moss max-w-xl leading-relaxed">
            Slide to estimate the monthly outgo on your home or commercial property loan.
            Then talk to us — we'll find you a better rate.
          </p>
        </Reveal>

        <div className="mt-16 grid lg:grid-cols-12 gap-8 items-stretch">
          <Reveal className="lg:col-span-7">
            <div className="h-full bg-white border border-black/5 rounded-3xl p-8 md:p-12 shadow-sm flex flex-col gap-10">
              <Field label="Loan amount" display={toWords(amount)} min={500000} max={50000000} step={100000}
                value={amount} onChange={setAmount} testid="emi-amount" minLabel="₹5 L" maxLabel="₹5 Cr" />
              <Field label="Interest rate (p.a.)" display={`${rate.toFixed(1)}%`} min={6} max={15} step={0.1}
                value={rate} onChange={setRate} testid="emi-rate" minLabel="6%" maxLabel="15%" />
              <Field label="Tenure" display={`${years} yrs`} min={1} max={30} step={1}
                value={years} onChange={setYears} testid="emi-tenure" minLabel="1 yr" maxLabel="30 yrs" />
            </div>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-5">
            <div className="h-full bg-forest text-white rounded-3xl p-8 md:p-12 flex flex-col justify-between shadow-xl shadow-forest/20">
              <div>
                <div className="flex items-center gap-2.5 text-mint-soft">
                  <Calculator size={20} weight="duotone" />
                  <span className="text-xs font-bold tracking-[0.2em] uppercase">Your estimate</span>
                </div>
                <p className="mt-6 text-sm text-white/60">Monthly EMI</p>
                <motion.p key={Math.round(emi)} initial={{ opacity: 0.4, y: 6 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }} className="font-serif text-5xl md:text-6xl tracking-tight text-white"
                  data-testid="emi-result">
                  {inr(emi)}
                </motion.p>

                <div className="mt-8">
                  <div className="h-2.5 rounded-full bg-white/10 overflow-hidden flex" data-testid="emi-breakdown-bar">
                    <motion.div className="h-full bg-mint" animate={{ width: `${principalPct}%` }}
                      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }} />
                  </div>
                  <div className="mt-3 flex justify-between text-xs text-white/60">
                    <span className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-mint inline-block" /> Principal {toWords(amount)}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-white/30 inline-block" /> Interest {toWords(interest)}
                    </span>
                  </div>
                </div>

                <div className="mt-8 grid grid-cols-2 gap-4">
                  <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
                    <p className="text-xs text-white/60">Total interest</p>
                    <p className="mt-1 font-serif text-xl text-white" data-testid="emi-total-interest">{toWords(interest)}</p>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
                    <p className="text-xs text-white/60">Total payment</p>
                    <p className="mt-1 font-serif text-xl text-white" data-testid="emi-total-payment">{toWords(total)}</p>
                  </div>
                </div>
              </div>

              <motion.a href="#contact" data-testid="emi-cta-button" whileTap={{ scale: 0.97 }}
                className="mt-10 inline-flex items-center justify-center gap-2 bg-mint text-forest-deep font-semibold px-8 py-4 rounded-full hover:bg-mint-soft transition-colors duration-300">
                Get a better rate <ArrowRight size={16} weight="bold" />
              </motion.a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <p className="mt-8 text-xs text-moss/80 max-w-2xl">
            Indicative estimate only. Actual EMI depends on the lender's sanctioned rate,
            tenure and charges. Loans subject to partner bank / NBFC credit approval.
          </p>
        </Reveal>
      </div>
    </section>
  );
};
